import memoize from 'lodash/memoize';
import { useAuthStore } from '@/stores/auth.ts';
import { moduleService } from '@/models/module-service';

export class Auth {
  can = memoize((ability) => {
    if (!this.getAuthStore().user) {
      return false;
    }

    const company = this.getAuthStore().company;

    if (!company) {
      // user has not set an active company
      if (this.getAuthStore().otherCompanies.length) {
        return false;
      }

      // user is creating his first company
      return true;
    }

    if (company.owner) {
      return true;
    }

    return this._check(ability, 'company', this.getAuthStore().company.id);
  }, (value) => {
    if (!this.getAuthStore().user) {
      return 'null';
    }

    if (!this.getAuthStore().company) {
      return `${value}|null`;
    }

    return `${value}|${this.getAuthStore().company.id}`;
  });

  canAny(abilities) {
    const passed = abilities.filter(ability => this.can(ability));

    return Boolean(passed.length);
  }

  // Warehouse features require both the permission and the module being available.
  canWarehouse() {
    return this.can('warehouse.index') && moduleService.can('warehouse');
  }

  // Picking products with their warehouse quantities only requires the module. Without the
  // `warehouse.index` permission the products are still listed with quantities; the backend
  // narrows them down when the role is limited to specific warehouses.
  canWarehouseProducts() {
    return Boolean(moduleService.can('warehouse'));
  }

  canSub(ability) {
    if (!this.getAuthStore().user) {
      return false;
    }

    if (this.getAuthStore().user.permissions.subscription.hasOwnProperty(ability)) {
      return this.getAuthStore().user.permissions.subscription[ability];
    }

    return false;
  }

  _check(ability, model = null, id = null) {
    const identifiers = this._compileAbilityIdentifiers(ability, model, id);
    let granted = false;

    for (const requested of identifiers) {
      if (this.getAuthStore().user.permissions.forbidden.indexOf(requested) >= 0) {
        return false;
      }

      if (this.getAuthStore().user.permissions.allowed.indexOf(requested) >= 0) {
        granted = true;
      }
    }

    return granted;
  }

  _compileAbilityIdentifiers(ability, model = null, id = null) {
    const name = ability.toLowerCase();

    if (model === null) {
      return [name, '*-*', '*'];
    }

    if (model === '*') {
      return [`${name}-*`, '*-*'];
    }

    const modelName = model.toLowerCase();
    const abilities = [
      `${name}-${modelName}`,
      `${name}-*`,
      `*-${modelName}`,
      '*-*',
    ];

    if (id !== null) {
      abilities.push(`${name}-${modelName}-${id}`);
      abilities.push(`*-${modelName}-${id}`);
    }

    return abilities;
  }

  getAuthStore() {
    return useAuthStore();
  }
}

export const authService = new Auth();
